import { companyName, IS_MAIN_COMPANY } from 'config'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTitle } from 'utils/pageTitle'

import { AccountExecutives } from './AccountExecutives'
import { AiConnectors } from './AiConnectors'
import { ApiKeys } from './ApiKeys'
import { AccountVerificationType, BrokerVerificationCategory } from './BrokerVerificationCategory'
import { CompanyBroadcast } from './CompanyBroadcast'
import { Configuration } from './Configuration/index'
import { Credentials } from './Credentials'
import { CustomizeApplication } from './CustomizeApplication'
import { DocumentCategories } from './DocumentCategories'
import { HomePageCreditBox } from './HomePageCreditBox'
import { LandingLearnMore } from './LandingLearnMore'
import { LoginPageNotifications } from './LoginPageNotifications'
import { PagePermissions } from './PagePermissions'
import { Permissions } from './Permissions'
import { Projects } from './Projects'
import { ResourceBroadcast } from './ResourceBroadcast'
import { ServerLog } from './ServerLog'
import { SingleSignOn } from './SingleSignOn'
import { SlackTeamsChannels } from './SlackTeamsChannels'
import { StripeManagements } from './StripeManage'
import { Survey } from './Survey'
import { Utm } from './Utm'

type menuType = {
  [key: string]: string //fix this
}

export default function AdminTools() {
  useTitle(`Admin Tools - ${companyName}`)

  const { menu = '' } = useParams() as any
  const navigate = useHistory()
  const [selectedMenu, setSelectedMenu] = useState(menu)
  const [menus, setMenus] = useState<menuType>({})
  const [changed, setChanged] = useState(false)

  const { hasPermission } = usePermissions()

  useEffect(() => {
    let temp: menuType = {}
    if (hasPermission('AdminTools')) {
      temp = {
        pagePermissions: 'Page Permissions',
        homePage: 'Home Page Credit Box',
        loginModal: 'Login Page Notifications',
        companyBroadcast: 'Company Broadcast',
        contractorApprovalVerification: 'Contractor Approval Categories',
        resourcesBroadcast: 'Resources Broadcast',
        stripeManage: 'Stripe Management',
        documentCategories: 'Document Categories',
        configuration: 'Configuration',
        projects: 'Projects',
        credentials: 'AMC Credentials',
        customizeApplication: 'Customize Application',
        utm: 'UTM',
        serverLogs: 'Server Logs',
        slackTeamsChannels: 'Slack / Teams Channels',
        aiConnectors: 'AI Connectors',
        singleSignOn: 'Single Sign-On',
      }
      if (IS_MAIN_COMPANY) delete temp.pagePermissions
    }
    // Issuing credentials is its own permission, so it can be held apart from general Admin Tools.
    // It is new, which means admin-only until an admin grants it in Admin Tools > Permissions.
    if (hasPermission('AdminTools.ApiKeys')) {
      temp = {
        ...temp,
        apiKeys: 'API Keys',
      }
    }
    if (hasPermission('AdminTools.Permissions')) {
      temp = {
        permissions: 'Permissions',
        ...temp,
      }
    }
    if (hasPermission('AdminTools.Reviews')) {
      temp = {
        ...temp,
        reviews: 'Reviews',
        accountExecutives: 'Account Executives',
        landingLearnMore: 'Landing Learn More',
      }
    }

    removeNonPagePermissions(temp, ['admin_tool'])
    setMenus(temp)
  }, [])

  const setMenu = (menuItem: string, changed?: boolean) => {
    setSelectedMenu(menuItem)
    if (!changed) navigate.push(`/admin_tool/${menuItem}`)
  }

  const renderFragment = useMemo(() => {
    switch (selectedMenu) {
      case 'apiKeys':
        return <ApiKeys />

      case 'permissions':
      case 'permissionsByRole':
        return <Permissions />

      case 'pagePermissions':
        return <PagePermissions />

      case 'homePage':
        return <HomePageCreditBox />

      case 'loginModal':
        return <LoginPageNotifications />

      case 'companyBroadcast':
        return <CompanyBroadcast />

      case 'contractorApprovalVerification':
        return (
          <div key={selectedMenu}>
            <BrokerVerificationCategory type={AccountVerificationType.ContractorApproval} />
          </div>
        )

      case 'resourcesBroadcast':
        return <ResourceBroadcast />

      case 'stripeManage':
        return <StripeManagements />

      case 'documentCategories':
        return <DocumentCategories />

      case 'reviews':
        return <Survey />

      case 'accountExecutives':
        return <AccountExecutives />

      case 'landingLearnMore':
        return <LandingLearnMore />

      case 'configuration':
      case 'emailDNSSetup':
      case 'emailLogs':
      case 'expirationDatesTypes':
      case 'bankerSearch':
        return <Configuration setChanged={setChanged} />

      case 'singleSignOn':
        return <SingleSignOn />

      case 'projects':
        return <Projects />

      case 'credentials':
        return <Credentials />

      case 'customizeApplication':
      case 'borrowerPipeline':
      case 'brokerPipeline':
        return <CustomizeApplication setChanged={setChanged} />

      case 'utm':
      case 'utmAnalysis':
      case 'utmLogs':
        return <Utm />

      case 'serverLogs':
        return <ServerLog />

      case 'slackTeamsChannels':
        return <SlackTeamsChannels />

      case 'aiConnectors':
        return <AiConnectors />

      default: {
        return <div>Comming Soon...</div>
      }
    }
  }, [selectedMenu])

  useEffect(() => {
    const redirects: Record<string, string> = {
      permissionsByRole: '/admin_tool/permissions?tab=account',
      emailDNSSetup: '/admin_tool/configuration?section=emailSetup&tab=dns',
      emailLogs: '/admin_tool/configuration?section=emailSetup&tab=logs',
      expirationDatesTypes: '/admin_tool/configuration?section=underwriting&tab=expiration',
      borrowerPipeline: '/admin_tool/customizeApplication?tab=borrower',
      brokerPipeline: '/admin_tool/customizeApplication?tab=broker',
      utmAnalysis: '/admin_tool/utm?tab=analysis',
      utmLogs: '/admin_tool/utm?tab=logs',
      bankerSearch: '/admin_tool/configuration?section=partyEligibility',
    }
    if (redirects[menu]) {
      const current = new URLSearchParams(window.location.search)
      const keyword = current.get('keyword')
      const tab = current.get('tab')
      let target = redirects[menu]
      if (menu === 'utmLogs' && keyword) target += `&keyword=${encodeURIComponent(keyword)}`
      if (menu === 'bankerSearch' && tab) target += `&tab=${encodeURIComponent(tab)}`
      navigate.replace(target)
      return
    }
    let newMenu = menu
    if (!menu || !Object.keys(menus).includes(newMenu)) newMenu = Object.keys(menus)[0]
    if (!newMenu) return
    setSelectedMenu(newMenu)
    if (newMenu !== menu) navigate.replace(`/admin_tool/${newMenu}`)
  }, [menus, menu])

  const onChangeMenu = async (value: string) => {
    setMenu(value, changed)
    setChanged(false)
  }

  return (
    <div className="admin-tools-container py-6">
      <div className="admin-tools-wrapper max-w-screen-2xl m-auto grid grid-cols-12 gap-6 px-2">
        <div className="sidebar-left col-span-12 md:col-span-3 shrink-0 bg-white shadow1 rounded">
          <ul className="sidebar-items flex flex-col p-4 pb-20">
            {Object.keys(menus).map((item: string, index) => {
              return (
                <li
                  key={index}
                  onClick={() => {
                    onChangeMenu(item)
                  }}
                  className="border-b py-2"
                >
                  <p
                    className={`hover:underline cursor-pointer ${
                      selectedMenu === item ||
                      (item === 'permissions' && selectedMenu === 'permissionsByRole') ||
                      (item === 'configuration' &&
                        ['emailDNSSetup', 'emailLogs', 'expirationDatesTypes', 'bankerSearch'].includes(
                          selectedMenu,
                        )) ||
                      (item === 'customizeApplication' &&
                        ['borrowerPipeline', 'brokerPipeline'].includes(selectedMenu)) ||
                      (item === 'utm' && ['utmAnalysis', 'utmLogs'].includes(selectedMenu))
                        ? 'border px-4 py-1 bg-zinc-100'
                        : 'py-1'
                    }`}
                  >
                    {index + 1}. {menus[item as keyof typeof menus]}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
        <div className="content-right col-span-12 md:col-span-9 bg-white p-4 rounded shadow1">{renderFragment}</div>
      </div>
    </div>
  )
}
